import {
  Button,
  ChakraSelect,
  formatOptionsArray,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useGetAssigneeListApi } from 'apis/ticket-management/use-get-assignee-list-api';
import { BaseFilterButton } from 'components/base-table/filters';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TOption = {
  value: string;
  label: string;
};

type TInputs = {
  assignee: TOption | undefined;
};

const defaultValues: TInputs = {
  assignee: undefined,
};

export const TicketAssignedToFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const { isLoading, data } = useGetAssigneeListApi();
  const { data: assigneeListData } = data ?? {};

  // Update active filter button
  useEffect(() => {
    if (params.assignee) setIsActive.on();
    else setIsActive.off();
  }, [params.assignee]);

  const { control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update values
  const updateValues = () => {
    const selected = formatOptionsArray(assigneeListData, 'id', 'name')?.find((item) => {
      return item?.value === Number(params?.assignee);
    });

    setValue('assignee', selected ?? undefined);
  };

  useEffect(() => {
    updateValues();
  }, [params.assignee, assigneeListData]);

  const onSubmit = (data: TInputs) => {
    params.assignee = data.assignee?.value as string;

    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    updateValues();
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='assignee'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Assignee</FormLabel>
                <ChakraSelect
                  id='assignee'
                  isLoading={isLoading}
                  options={formatOptionsArray(assigneeListData, 'id', 'name')}
                  onChange={onChange}
                  name={name}
                  onBlur={onBlur}
                  value={value}
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
